import { useMemo, useState, type ReactNode } from 'react';
import {
  ArrowLeft,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Printer,
  Receipt,
  Search,
  Settings2,
  Trophy,
  Users,
} from 'lucide-react';
import {
  buildLedger,
  centresOf,
  filterLedger,
  ledgerToCsv,
  longDateLabel,
  monthKeyOfDate,
  monthLabel,
  monthsOf,
  rowFee,
  settle,
  shiftMonth,
  shortDateLabel,
  TX_KIND_LABEL,
  TX_METHOD_LABEL,
  TX_STATUS_LABEL,
  type ReportingSettings,
  type ReportTransaction,
  type ReportTxKind,
  type ReportTxStatus,
} from '../../engine/reporting';
import { formatNok } from '../../lib/money';

export interface ReportingViewProps {
  bookings: any[];
  activities: any[];
  /** Tournaments incl. live ones (archiveWithLive) — seriespill season fees. */
  tournaments: any[];
  /** Player database (names for activity sign-ups). */
  players: any[];
  clubInfo: any;
  bookingConfig: any;
  settings: ReportingSettings;
  /** Persists the fee model (Storage-first inside the hook). */
  onSaveSettings: (next: Partial<ReportingSettings>) => Promise<unknown>;
  onBack: () => void;
  /** «Now» — injectable for deterministic tests. */
  now?: Date;
}

type StatusFilter = 'all' | ReportTxStatus;

const STATUS_FILTERS: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'Alle' },
  { id: 'paid', label: 'Betalt' },
  { id: 'pending', label: 'Utestående' },
  { id: 'free', label: 'Gratis' },
  { id: 'refunded', label: 'Refundert' },
];

const STATUS_STYLE: Record<ReportTxStatus, { bg: string; fg: string }> = {
  paid: { bg: 'var(--lime-bg)', fg: '#3f6212' },
  pending: { bg: 'var(--orange-soft)', fg: '#9a3412' },
  free: { bg: 'var(--bg-soft)', fg: 'var(--ink-2)' },
  refunded: { bg: 'var(--rose-soft)', fg: '#9f1239' },
};

const KIND_ICON: Record<ReportTxKind, typeof Calendar> = {
  booking: Calendar,
  activity: Users,
  seriespill: Trophy,
};

const todayLabel = (d: Date) =>
  shortDateLabel(
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
  );

/**
 * Reporting — the centre's monthly settlement basis. Every booking, activity
 * sign-up and seriespill season fee is one receipt-like line; the month is
 * settled against the platform fee model (percentage per paid transaction +
 * a fixed monthly fee) into a net payout.
 */
export function ReportingView({
  bookings,
  activities,
  tournaments,
  players,
  clubInfo,
  bookingConfig,
  settings,
  onSaveSettings,
  onBack,
  now,
}: ReportingViewProps) {
  const nowDate = now || new Date();
  const ledger = useMemo(
    () => buildLedger({ bookings, activities, tournaments, players, clubInfo, bookingConfig }),
    [bookings, activities, tournaments, players, clubInfo, bookingConfig],
  );
  const centres = useMemo(() => centresOf(ledger, clubInfo), [ledger, clubInfo]);
  const months = useMemo(() => monthsOf(ledger), [ledger]);

  const [centre, setCentre] = useState<string>(centres[0]);
  const [month, setMonth] = useState<string>(monthKeyOfDate(nowDate));
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  const activeCentre = centres.includes(centre) ? centre : centres[0];
  const monthRows = useMemo(
    () => filterLedger(ledger, { month, centre: activeCentre, includeDemo: settings.includeDemo }),
    [ledger, month, activeCentre, settings.includeDemo],
  );
  const settlement = useMemo(() => settle(monthRows, settings), [monthRows, settings]);

  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return monthRows.filter(
      (r) =>
        (statusFilter === 'all' || r.status === statusFilter) &&
        (!q ||
          [r.title, r.detail, r.payer, r.reference || '', TX_KIND_LABEL[r.kind]]
            .join(' ')
            .toLowerCase()
            .includes(q)),
    );
  }, [monthRows, statusFilter, query]);

  // Group the visible rows per day for the ledger list (rows arrive newest first).
  const days = useMemo(() => {
    const out: { date: string; rows: ReportTransaction[]; sum: number }[] = [];
    visibleRows.forEach((r) => {
      let d = out[out.length - 1];
      if (!d || d.date !== r.date) {
        d = { date: r.date, rows: [], sum: 0 };
        out.push(d);
      }
      d.rows.push(r);
      if (r.status === 'paid') d.sum += r.amountNok;
    });
    return out;
  }, [visibleRows]);

  const currentMonth = monthKeyOfDate(nowDate);
  const hasOtherCentres = centres.length > 1;
  const isFuture = month > currentMonth;

  const downloadCsv = () => {
    const csv = ledgerToCsv(monthRows, settings, { centre: activeCentre, month });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporting-${activeCentre.replace(/[^\p{L}\p{N}]+/gu, '-').toLowerCase()}-${month}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 slide-up">
      <button onClick={onBack} className="btn-ghost mb-4 -ml-2 print:hidden">
        <ArrowLeft size={14} /> Hjem
      </button>

      {/* Title row */}
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1">
            Avregningsgrunnlag · {activeCentre}
          </div>
          <h1 className="font-display text-3xl font-semibold mb-1">Reporting</h1>
          <p className="text-ink-2 text-sm max-w-xl">
            Hver booking, påmelding og sesongavgift som en kvittering — samlet per måned, med
            plattformavgift og netto til senteret.
          </p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <button onClick={() => window.print()} className="btn-outline text-sm">
            <Printer size={14} /> Skriv ut
          </button>
          <button
            onClick={downloadCsv}
            className="btn-primary text-sm"
            disabled={monthRows.length === 0}
            title={monthRows.length === 0 ? 'Ingen transaksjoner denne måneden' : 'Last ned CSV'}
          >
            <Download size={14} /> Last ned CSV
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="card p-3 mb-5 flex flex-wrap items-center gap-3 print:hidden">
        {hasOtherCentres && (
          <label className="flex items-center gap-2 text-sm">
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
              Senter
            </span>
            <select
              className="input text-sm py-1.5"
              value={activeCentre}
              onChange={(e) => setCentre(e.target.value)}
              aria-label="Velg senter"
            >
              {centres.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="flex items-center gap-1">
          <button
            onClick={() => setMonth(shiftMonth(month, -1))}
            className="btn-ghost p-1.5"
            aria-label="Forrige måned"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="font-display text-lg font-semibold min-w-[11rem] text-center">
            {monthLabel(month)}
          </div>
          <button
            onClick={() => setMonth(shiftMonth(month, 1))}
            className="btn-ghost p-1.5"
            aria-label="Neste måned"
          >
            <ChevronRight size={16} />
          </button>
          {month !== currentMonth && (
            <button onClick={() => setMonth(currentMonth)} className="btn-ghost text-xs">
              Denne måneden
            </button>
          )}
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-1 flex-wrap">
          {STATUS_FILTERS.map((f) => {
            const active = statusFilter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setStatusFilter(f.id)}
                aria-pressed={active}
                className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                  active
                    ? 'bg-ink text-white border-ink'
                    : 'bg-paper text-ink-2 border-line hover:bg-bg-soft'
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        <label className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-3" />
          <input
            className="input text-sm py-1.5 w-48"
            style={{ paddingLeft: '2rem' }}
            placeholder="Søk betaler, bane …"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Søk i transaksjoner"
          />
        </label>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <Stat
          label="Brutto omsetning"
          value={formatNok(settlement.grossNok)}
          hint={`${settlement.paidCount} betalt${settlement.paidCount === 1 ? '' : 'e'} transaksjon${settlement.paidCount === 1 ? '' : 'er'}`}
        />
        <Stat
          label="Utestående"
          value={formatNok(settlement.pendingNok)}
          hint={
            settlement.pendingCount
              ? `${settlement.pendingCount} venter på betaling`
              : 'Ingenting utestående'
          }
          tone={settlement.pendingCount ? 'warn' : undefined}
        />
        <Stat
          label="Plattformavgift"
          value={formatNok(settlement.totalFeeNok)}
          hint={`${fmtPct(settings.feePct)} % per transaksjon + ${formatNok(settlement.fixedFeeNok)} fast`}
        />
        <Stat
          label="Til utbetaling"
          value={formatNok(settlement.netNok)}
          hint={
            settlement.grossNok > 0
              ? `Effektiv avgift ${fmtPct(settlement.effectivePct)} %`
              : 'Ingen betalte transaksjoner'
          }
          tone="accent"
        />
      </div>

      <div className="grid lg:grid-cols-[1fr_22rem] gap-5 items-start">
        {/* Ledger */}
        <section>
          <div className="flex items-baseline justify-between mb-2 px-1">
            <h2 className="font-display text-lg font-semibold">Transaksjoner</h2>
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
              {visibleRows.length} av {monthRows.length}
            </span>
          </div>

          {monthRows.length === 0 && (
            <div className="card p-8 text-center">
              <Receipt size={28} className="mx-auto mb-2 text-ink-4" />
              <div className="font-display text-lg font-semibold mb-1">
                Ingen transaksjoner i {monthLabel(month).toLowerCase()}
              </div>
              <p className="text-ink-2 text-sm max-w-sm mx-auto">
                {isFuture
                  ? 'Måneden har ikke startet. Bookinger og påmeldinger dukker opp her etter hvert som de registreres.'
                  : 'Ingen bookinger, påmeldinger eller sesongavgifter er registrert på dette senteret denne måneden.'}
              </p>
              {months.length > 0 && !months.includes(month) && (
                <button onClick={() => setMonth(months[0])} className="btn-outline text-sm mt-4">
                  Gå til {monthLabel(months[0]).toLowerCase()}
                </button>
              )}
            </div>
          )}

          {monthRows.length > 0 && visibleRows.length === 0 && (
            <div className="card p-6 text-center text-sm text-ink-2">
              Ingen transaksjoner matcher filteret.
            </div>
          )}

          <div className="space-y-4">
            {days.map((d) => (
              <div key={d.date}>
                <div className="flex items-baseline justify-between px-1 mb-1.5">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
                    {longDateLabel(d.date)}
                  </div>
                  <div className="font-mono text-[11px] text-ink-2">{formatNok(d.sum)}</div>
                </div>
                <div className="card divide-y divide-line overflow-hidden">
                  {d.rows.map((r) => (
                    <TxRow
                      key={r.id}
                      row={r}
                      fees={settings}
                      open={openId === r.id}
                      onToggle={() => setOpenId(openId === r.id ? null : r.id)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Statement + settings */}
        <aside className="space-y-4 lg:sticky lg:top-4">
          <Statement
            centre={activeCentre}
            clubInfo={clubInfo}
            month={month}
            settlement={settlement}
            settings={settings}
            generated={todayLabel(nowDate)}
          />

          <div className="card print:hidden">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="w-full flex items-center justify-between px-4 py-3 text-left"
              aria-expanded={showSettings}
            >
              <span className="flex items-center gap-2 text-sm font-semibold">
                <Settings2 size={14} /> Avgiftsmodell
              </span>
              <ChevronDown
                size={14}
                className={`text-ink-3 transition-transform ${showSettings ? 'rotate-180' : ''}`}
              />
            </button>
            {showSettings && <FeeSettings settings={settings} onSave={onSaveSettings} />}
          </div>
        </aside>
      </div>
    </div>
  );
}

// ─── pieces ───

const fmtPct = (n: number) =>
  Number.isFinite(n) ? String(Math.round(n * 100) / 100).replace('.', ',') : '0';

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint: string;
  tone?: 'accent' | 'warn';
}) {
  const style =
    tone === 'accent'
      ? { background: 'var(--lime-bg)', borderColor: 'var(--lime-deep)' }
      : tone === 'warn'
        ? { background: 'var(--orange-soft)' }
        : undefined;
  return (
    <div className="card p-4" style={style}>
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1">{label}</div>
      <div className="font-display text-2xl font-semibold tabular-nums">{value}</div>
      <div className="text-xs text-ink-2 mt-0.5">{hint}</div>
    </div>
  );
}

function StatusPill({ status }: { status: ReportTxStatus }) {
  const s = STATUS_STYLE[status];
  return (
    <span
      className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ background: s.bg, color: s.fg }}
    >
      {TX_STATUS_LABEL[status]}
    </span>
  );
}

function TxRow({
  row,
  fees,
  open,
  onToggle,
}: {
  row: ReportTransaction;
  fees: ReportingSettings;
  open: boolean;
  onToggle: () => void;
}) {
  const Icon = KIND_ICON[row.kind];
  const fee = rowFee(row, fees);
  const muted = row.status !== 'paid';
  const meta = [TX_KIND_LABEL[row.kind], row.payer];
  if (row.method !== 'none') meta.push(TX_METHOD_LABEL[row.method]);
  return (
    <div className={row.demo ? 'opacity-60' : ''}>
      <button
        onClick={onToggle}
        className="w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-bg-soft/60 transition-colors"
        aria-expanded={open}
      >
        <span
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'var(--bg-soft)', color: 'var(--ink-2)' }}
        >
          <Icon size={14} />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium truncate">
            {row.title}
            {row.demo && <span className="text-ink-3 font-normal"> · demo</span>}
          </span>
          <span className="block text-xs text-ink-2 truncate">{meta.join(' · ')}</span>
        </span>
        <span className="text-right flex-shrink-0">
          <span
            className={`block font-mono text-sm tabular-nums ${muted ? 'text-ink-3' : 'text-ink'} ${row.status === 'refunded' ? 'line-through' : ''}`}
          >
            {formatNok(row.amountNok)}
          </span>
          <StatusPill status={row.status} />
        </span>
      </button>
      {open && (
        <div
          className="px-4 pb-4 pt-1 text-sm"
          style={{ background: 'var(--bg)', borderTop: '1px dashed var(--line)' }}
        >
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-2 flex items-center gap-1.5">
            <Receipt size={11} /> Kvittering
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <Line k="Dato" v={`${shortDateLabel(row.date)}${row.time ? ` · ${row.time}` : ''}`} />
            <Line k="Beskrivelse" v={row.title} />
            <Line k="Detalj" v={row.detail} />
            <Line k="Betaler" v={row.payer} />
            <Line k="Betalingsmåte" v={TX_METHOD_LABEL[row.method]} />
            {row.reference && (
              <Line k="Referanse" v={<span className="font-mono text-xs">{row.reference}</span>} />
            )}
            {row.paidAtMs != null && (
              <Line
                k="Betalt"
                v={new Date(row.paidAtMs).toLocaleString('nb-NO', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              />
            )}
            <Line k="Status" v={TX_STATUS_LABEL[row.status]} />
          </dl>
          <div
            className="mt-3 pt-2 font-mono text-xs space-y-1"
            style={{ borderTop: '1px dashed var(--line)' }}
          >
            <Amt k="Beløp" v={formatNok(row.amountNok)} />
            <Amt k={`Plattformavgift ${fmtPct(fees.feePct)} %`} v={`− ${formatNok(fee)}`} />
            <Amt
              k="Netto til senteret"
              v={formatNok(row.status === 'paid' ? row.amountNok - fee : 0)}
              strong
            />
          </div>
        </div>
      )}
    </div>
  );
}

const Line = ({ k, v }: { k: string; v: ReactNode }) => (
  <>
    <dt className="text-ink-3 text-xs">{k}</dt>
    <dd className="text-ink">{v}</dd>
  </>
);

const Amt = ({ k, v, strong }: { k: string; v: string; strong?: boolean }) => (
  <div className={`flex justify-between gap-3 ${strong ? 'font-semibold text-ink' : 'text-ink-2'}`}>
    <span>{k}</span>
    <span className="tabular-nums">{v}</span>
  </div>
);

function Statement({
  centre,
  clubInfo,
  month,
  settlement,
  settings,
  generated,
}: {
  centre: string;
  clubInfo: any;
  month: string;
  settlement: ReturnType<typeof settle>;
  settings: ReportingSettings;
  generated: string;
}) {
  const kinds = (Object.keys(TX_KIND_LABEL) as ReportTxKind[]).filter(
    (k) => settlement.byKind[k].count > 0,
  );
  const methods = (Object.keys(TX_METHOD_LABEL) as (keyof typeof TX_METHOD_LABEL)[]).filter(
    (m) => settlement.byMethod[m].count > 0,
  );
  return (
    <div className="card-elev p-5" data-testid="statement">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3">Avregning</div>
          <div className="font-display text-xl font-semibold leading-tight">{centre}</div>
          {clubInfo?.address && <div className="text-xs text-ink-2">{clubInfo.address}</div>}
        </div>
        <Receipt size={20} className="text-ink-3 flex-shrink-0" />
      </div>
      <div className="flex justify-between text-sm mb-3">
        <span className="text-ink-2">Periode</span>
        <span className="font-medium">{monthLabel(month)}</span>
      </div>

      <div
        className="font-mono text-xs space-y-1"
        style={{ borderTop: '1px dashed var(--line)', paddingTop: 10 }}
      >
        {kinds.length === 0 && <div className="text-ink-3">Ingen betalte transaksjoner</div>}
        {kinds.map((k) => (
          <Amt
            key={k}
            k={`${TX_KIND_LABEL[k]} × ${settlement.byKind[k].count}`}
            v={formatNok(settlement.byKind[k].grossNok)}
          />
        ))}
        <div style={{ borderTop: '1px dashed var(--line)', margin: '8px 0' }} />
        <Amt k="Brutto omsetning" v={formatNok(settlement.grossNok)} strong />
        <Amt
          k={`Transaksjonsavgift ${fmtPct(settings.feePct)} %`}
          v={`− ${formatNok(settlement.pctFeeNok)}`}
        />
        <Amt k="Fast månedsavgift" v={`− ${formatNok(settlement.fixedFeeNok)}`} />
        <div style={{ borderTop: '2px solid var(--ink)', margin: '8px 0' }} />
        <div
          className="flex justify-between gap-3 font-display text-base font-semibold rounded-lg px-2 py-1.5 -mx-2"
          style={{ background: 'var(--lime-bg)' }}
        >
          <span>Til utbetaling</span>
          <span className="tabular-nums">{formatNok(settlement.netNok)}</span>
        </div>
      </div>

      {(settlement.pendingCount > 0 || settlement.refundedCount > 0 || methods.length > 0) && (
        <div
          className="font-mono text-xs space-y-1 mt-4 pt-3 text-ink-2"
          style={{ borderTop: '1px dashed var(--line)' }}
        >
          {methods.map((m) => (
            <Amt
              key={m}
              k={`${TX_METHOD_LABEL[m]} × ${settlement.byMethod[m].count}`}
              v={formatNok(settlement.byMethod[m].grossNok)}
            />
          ))}
          {settlement.pendingCount > 0 && (
            <Amt
              k={`Utestående × ${settlement.pendingCount}`}
              v={formatNok(settlement.pendingNok)}
            />
          )}
          {settlement.refundedCount > 0 && (
            <Amt
              k={`Refundert × ${settlement.refundedCount}`}
              v={formatNok(settlement.refundedNok)}
            />
          )}
        </div>
      )}

      <div
        className="mt-4 pt-3 text-[11px] text-ink-3 leading-relaxed"
        style={{ borderTop: '1px dashed var(--line)' }}
      >
        Generert {generated}. Utestående og refunderte beløp inngår ikke i brutto. Avregningen er et
        grunnlag — fakturaen fra plattformen bygger på disse tallene.
      </div>
    </div>
  );
}

function FeeSettings({
  settings,
  onSave,
}: {
  settings: ReportingSettings;
  onSave: (next: Partial<ReportingSettings>) => Promise<unknown>;
}) {
  const [pct, setPct] = useState(String(settings.feePct));
  const [fixed, setFixed] = useState(String(settings.monthlyFeeNok));
  const [includeDemo, setIncludeDemo] = useState(settings.includeDemo);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dirty =
    pct !== String(settings.feePct) ||
    fixed !== String(settings.monthlyFeeNok) ||
    includeDemo !== settings.includeDemo;

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave({
        feePct: Number(pct.replace(',', '.')),
        monthlyFeeNok: Number(fixed.replace(',', '.')),
        includeDemo,
      });
    } catch {
      setError('Kunne ikke lagre avgiftsmodellen.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-4 pb-4 space-y-3 text-sm" style={{ borderTop: '1px solid var(--line)' }}>
      <p className="text-xs text-ink-2 pt-3">
        Plattformen tar en liten prosent av hver betalte transaksjon i tillegg til et fast
        månedsbeløp per senter. Endringer gjelder alle måneder som vises.
      </p>
      <label className="block">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
          Transaksjonsavgift (%)
        </span>
        <input
          className="input w-full mt-1"
          inputMode="decimal"
          value={pct}
          onChange={(e) => setPct(e.target.value)}
          aria-label="Transaksjonsavgift i prosent"
        />
      </label>
      <label className="block">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
          Fast månedsavgift (kr)
        </span>
        <input
          className="input w-full mt-1"
          inputMode="numeric"
          value={fixed}
          onChange={(e) => setFixed(e.target.value)}
          aria-label="Fast månedsavgift i kroner"
        />
      </label>
      <label className="flex items-center gap-2 text-xs text-ink-2">
        <input
          type="checkbox"
          checked={includeDemo}
          onChange={(e) => setIncludeDemo(e.target.checked)}
        />
        Ta med demo-bookinger i grunnlaget
      </label>
      {error && (
        <div className="text-xs" style={{ color: 'var(--danger)' }}>
          {error}
        </div>
      )}
      <button onClick={save} className="btn-primary text-sm w-full" disabled={!dirty || saving}>
        {saving ? 'Lagrer …' : 'Lagre avgiftsmodell'}
      </button>
    </div>
  );
}
